import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { listHardwareRequests } from '@/api/hardwareRequests';
import { formatDate } from '@/lib/dateFormat';
import { decisionReading, deliveryReading, isForBranch, toneClass } from '@/lib/hardwareRequest';
import { useSort, sortRows, matchesQuery } from '@/lib/sortTable';
import { SortHeader } from '@/components/ui/SortHeader';
import { DesktopTable, MobileCards, MobileCard, CardField } from '@/components/common/responsiveList';
import type { HardwareRequestApprovalStatus, HardwareRequestSummary } from '@/types/oscar';

const inputCls = 'mt-1 rounded border border-border bg-white px-2 py-1.5 text-sm font-normal';

/**
 * Hardware request registry — every request raised in Manage.Core.
 *
 * Read only. This is Phase 1 of `OnsiteCore-hardware-lifecycle-design.md`:
 * the register plus, on the detail, what Oscar already knows about the device
 * somebody is asking to replace. No approval, no allocation — those are Phase 2
 * and need the `oscar-asset-manager` role, which does not exist yet.
 */
export function HardwareRequestsPage() {
  const [requestType, setRequestType] = useState('');
  // ⛔ Opens on Pending, not on everything. The queue's job is "what needs me",
  // and a list that opens on 300 decided requests buries the six that do not.
  const [status, setStatus] = useState<HardwareRequestApprovalStatus | 'all'>('Pending');
  const [search, setSearch] = useState('');
  const { sortKey, sortDir, onSort } = useSort('submitted');

  const query = useQuery({
    queryKey: ['hardware-requests', { requestType, status }],
    queryFn: ({ signal }) => listHardwareRequests({ requestType, status }, signal),
  });

  const colValue = (r: HardwareRequestSummary, k: string): unknown =>
    ({
      request: r.SubmissionId,
      submitted: r.SubmittedAt ?? '',
      employee: r.EmployeeName,
      type: r.RequestType,
      reason: r.Reason,
      branch: r.BranchName ?? '',
      decision: r.ApprovalStatus,
    } as Record<string, unknown>)[k];

  const rows = sortRows(
    (query.data ?? []).filter((r) =>
      matchesQuery(search, [r.EmployeeName, r.SubmitterName, r.Devices, r.BranchName]),
    ),
    (r) => colValue(r, sortKey),
    sortDir,
  );

  const failedOscarWrites = (query.data ?? []).filter((r) => r.ReplacedAssetErrorCount > 0).length;

  return (
    <section>
      <header className="mb-5">
        <h1 className="font-heading text-2xl font-bold text-navy">Hardware requests</h1>
        <p className="mt-1 text-sm text-darkGrey/80">
          Every request raised through the Hardware and Mobile Device Request form. Open one to see
          what Oscar knows about the device being replaced.
        </p>
      </header>

      {/*
        An asset that failed the manage → oscar lifecycle write is stuck in the
        state it was already in, and nothing else reports it. Surfacing the count
        here rather than only on the detail means it does not depend on somebody
        happening to open the right request.
      */}
      {failedOscarWrites > 0 && (
        <div className="mb-4 flex items-start gap-2 rounded border border-red/30 bg-red/5 px-3 py-2 text-sm text-red">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <span>
            {failedOscarWrites} request{failedOscarWrites === 1 ? '' : 's'} could not update Oscar.
            Those assets are still in their previous lifecycle state.
          </span>
        </div>
      )}

      <div className="mb-3 flex flex-wrap items-end gap-3">
        <label className="flex flex-col text-xs font-semibold text-darkGrey">
          Type
          <select
            aria-label="Filter by request type"
            value={requestType}
            onChange={(e) => setRequestType(e.target.value)}
            className={inputCls}
          >
            <option value="">All</option>
            <option value="New">New device(s)</option>
            <option value="Replacement">Replacement device(s)</option>
          </select>
        </label>
        <label className="flex flex-col text-xs font-semibold text-darkGrey">
          Decision
          <select
            aria-label="Filter by decision"
            value={status}
            onChange={(e) => setStatus(e.target.value as HardwareRequestApprovalStatus | 'all')}
            className={inputCls}
          >
            <option value="Pending">Awaiting decision</option>
            <option value="Approved">Approved</option>
            <option value="Declined">Declined</option>
            <option value="Referred">Referred back</option>
            <option value="all">All</option>
          </select>
        </label>
        <label className="flex flex-1 flex-col text-xs font-semibold text-darkGrey">
          Search
          <input
            aria-label="Search hardware requests"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Employee, submitter, device or branch"
            className={inputCls}
          />
        </label>
      </div>

      <DesktopTable className="overflow-x-auto rounded border border-border bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-bg text-left text-xs uppercase tracking-wide text-darkGrey/70">
            <tr>
              <SortHeader label="Request" columnKey="request" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
              <SortHeader label="Submitted" columnKey="submitted" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
              <SortHeader label="For" columnKey="employee" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
              <SortHeader label="Type" columnKey="type" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
              <SortHeader label="Reason" columnKey="reason" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
              <th className="px-3 py-2">Devices</th>
              <SortHeader label="Branch" columnKey="branch" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
              <SortHeader label="Decision" columnKey="decision" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
              <th className="px-3 py-2">Delivery</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {query.isLoading && (
              <tr><td colSpan={10} className="px-3 py-6 text-center text-darkGrey/60">Loading…</td></tr>
            )}
            {query.isError && (
              <tr><td colSpan={10} className="px-3 py-6 text-center text-red">Could not load hardware requests.</td></tr>
            )}
            {rows.length === 0 && !query.isLoading && !query.isError && (
              <tr><td colSpan={10} className="px-3 py-6 text-center text-darkGrey/60">{status === 'Pending' ? 'Nothing awaiting a decision.' : 'No hardware requests.'}</td></tr>
            )}
            {rows.map((r) => {
              const delivery = deliveryReading(r);
              const decision = decisionReading(r.ApprovalStatus);
              return (
                <tr key={r.SubmissionId} className="border-t border-border hover:bg-bg/60">
                  {/* ⛔ THE REQUEST NUMBER, FIRST AND CLICKABLE. It is how a
                      request is referred to everywhere else — the detail page
                      header, the Zendesk ticket, the cost line's description —
                      and the list was the one place that did not show it. */}
                  <td className="px-3 py-2">
                    <Link
                      to={`/hardware-requests/${r.SubmissionId}`}
                      className="font-semibold text-navy hover:underline"
                    >
                      #{r.SubmissionId}
                    </Link>
                  </td>
                  <td className="px-3 py-2 text-darkGrey/80">{formatDate(r.SubmittedAt)}</td>
                  <td className="px-3 py-2 font-medium text-navy">
                    {r.EmployeeName}
                    {/* ⛔ The name alone is indistinguishable from a person's.
                        Without this an approver reads "Perth" as somebody
                        having typed a place into the wrong field. */}
                    {isForBranch(r.BeneficiaryKind) && (
                      <span className="ml-2 rounded-full bg-lightBlue/60 px-2 py-0.5 text-xs font-semibold text-navy">For a branch</span>
                    )}
                    {!r.IsForSelf && (
                      <span className="ml-2 text-xs font-normal text-darkGrey/60">
                        raised by {r.SubmitterName ?? r.SubmitterEmail}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-darkGrey/80">{r.RequestTypeLabel}</td>
                  <td className="px-3 py-2 text-darkGrey/80">{r.Reason}</td>
                  <td className="px-3 py-2 text-darkGrey/80">{r.Devices ?? '—'}</td>
                  <td className="px-3 py-2 text-darkGrey/80">{r.BranchName ?? '—'}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${toneClass[decision.tone]}`}>
                      {decision.label}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${toneClass[delivery.tone]}`}>
                      {delivery.label}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Link to={`/hardware-requests/${r.SubmissionId}`} className="text-xs font-semibold text-navy hover:underline">
                      Open
                    </Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </DesktopTable>

      {/* Mobile (<md): requests as stacked cards. */}
      <MobileCards>
        {query.isLoading && <p className="px-1 py-6 text-center text-sm text-darkGrey/60">Loading…</p>}
        {query.isError && <p className="px-1 py-6 text-center text-sm text-red">Could not load hardware requests.</p>}
        {rows.length === 0 && !query.isLoading && !query.isError && (
          <p className="px-1 py-6 text-center text-sm text-darkGrey/60">No hardware requests.</p>
        )}
        {rows.map((r) => {
          const delivery = deliveryReading(r);
          const decision = decisionReading(r.ApprovalStatus);
          return (
            <MobileCard key={r.SubmissionId}>
              <div className="flex items-start justify-between gap-2">
                <span className="text-sm font-semibold text-navy">
                  {r.EmployeeName}
                  {isForBranch(r.BeneficiaryKind) && (
                    <span className="ml-2 rounded-full bg-lightBlue/60 px-2 py-0.5 text-xs font-semibold text-navy">For a branch</span>
                  )}
                </span>
                <span className={`inline-flex shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold ${toneClass[decision.tone]}`}>
                  {decision.label}
                </span>
              </div>
              <div className="mt-2 border-t border-border pt-2">
                {/* ⚠ The card list is aria-hidden on desktop, so this is a
                    SECOND place the number has to appear — jsdom renders both
                    layouts and only one of them is ever visible. */}
                <CardField label="Request">
                  <Link to={`/hardware-requests/${r.SubmissionId}`} className="font-semibold text-navy hover:underline">
                    #{r.SubmissionId}
                  </Link>
                </CardField>
                <CardField label="Submitted">{formatDate(r.SubmittedAt)}</CardField>
                <CardField label="Type">{r.RequestTypeLabel}</CardField>
                <CardField label="Reason">{r.Reason}</CardField>
                <CardField label="Devices">{r.Devices ?? '—'}</CardField>
                <CardField label="Branch">{r.BranchName ?? '—'}</CardField>
                <CardField label="Delivery">{delivery.label}</CardField>
              </div>
              <div className="mt-2 text-right">
                <Link to={`/hardware-requests/${r.SubmissionId}`} className="text-xs font-semibold text-navy hover:underline">
                  Open
                </Link>
              </div>
            </MobileCard>
          );
        })}
      </MobileCards>
    </section>
  );
}
